import type { BrowseRow } from "@/lib/cards";

import { FLAGS, STATE_LABEL, dueLabel, flagName } from "./query";

/**
 * One line in the Cards list — `9px 18px`, a `1px` divider between, and every column at a fixed
 * width so the eye can run down one of them. The gloss is the only flexible column, and it ellipses.
 */

const STATE_STYLE: Record<BrowseRow["state"], string> = {
  new: "border-accent-border bg-accent-tint text-accent-text",
  learning: "border-accent-border bg-accent-tint text-ink-3",
  young: "border-[color:var(--line)] bg-[color:var(--line-soft)] text-[color:var(--ink-3)]",
  mature: "border-line bg-line-soft text-ink-3",
  suspended: "border-line-strong bg-sunken text-ink-3",
};

function flagColour(flag: number): string | null {
  const found = FLAGS.find((f) => f.value === flag);
  return found ? `var(${found.token})` : null;
}

export interface CardRowProps {
  row: BrowseRow;
  selected: boolean;
  focused: boolean;
  onToggleSelect: () => void;
  onFocus: () => void;
  /** The row's right-click menu — built by `Cards`, which owns the actions. */
  onContextMenu?: (event: React.MouseEvent) => void;
  /** Two lines instead of eight columns. See `PhoneCardRow`. */
  isPhone?: boolean;
}

/**
 * **Eight fixed columns become two lines on a phone**, which the Mobile canvas states as a
 * principle — *"単語 · eight columns become a two-line row"* — and which is not a matter of
 * narrowing them: the desktop row spends 532px on fixed columns before the gloss gets any, so at
 * 390px everything after the reading is off the edge. It does not overflow the page (the row
 * clips), so the standing horizontal-scroll assertion never sees it; it just silently stops
 * showing you the state, the due date and the lapses.
 *
 * What the two lines carry is the frame's: the flag stripe and the headword with its reading on the
 * first, the gloss on the second, and the state chip with the due date right-aligned across both.
 * The tag column is the one thing dropped — `Enrich01 Freq:Top_10k JLPT_N5` is 10px mono metadata
 * that would take the whole second line from the gloss, and it is on the note when you open it.
 */
function PhoneCardRow({ row, selected, focused, onToggleSelect, onFocus, onContextMenu }: CardRowProps) {
  const flag = flagColour(row.flag);
  return (
    <div
      data-check="cards.row"
      onClick={onFocus}
      onContextMenu={onContextMenu}
      className={[
        "flex cursor-default items-start gap-[10px] border-b border-line-soft py-[10px] pl-[14px] pr-[14px] text-left",
        focused ? "bg-panel shadow-[inset_3px_0_0_var(--accent)]" : "",
      ].join(" ")}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        onClick={(e) => e.stopPropagation()}
        aria-label={`${row.headword} を選択`}
        className="mt-[3px] size-[17px] shrink-0 accent-[var(--accent)]"
      />
      <span
        aria-label={flag ? (flagName(row.flag) ?? undefined) : undefined}
        className="mt-[3px] h-[30px] w-[3px] shrink-0 rounded-[var(--r-2)]"
        style={{ background: flag ?? "transparent" }}
      />

      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="flex min-w-0 items-baseline gap-[8px]">
          <span className="truncate font-jp text-[19px]/[1.2] text-ink">{row.headword}</span>
          <span className="truncate font-jp text-[14px]/[1.2] text-ink-3">{row.reading}</span>
        </span>
        <span className="truncate text-[14px]/[1.35] text-ink-2">{row.gloss}</span>
      </span>

      {/* Right-aligned and stacked, so the two facts a reader scans for — what state it is in and
          when it is next due — line up down the edge of the list as a column would. */}
      <span className="flex shrink-0 flex-col items-end gap-[4px]">
        <span
          className={[
            "rounded-[var(--r-2)] border px-[6px] py-[1px] text-[14px]/[1.3] font-semibold",
            STATE_STYLE[row.state],
          ].join(" ")}
        >
          {STATE_LABEL[row.state]}
        </span>
        <span className="font-mono text-[14px]/[1.3] text-ink-4">{dueLabel(row)}</span>
      </span>
    </div>
  );
}

export function CardRow({
  row,
  selected,
  focused,
  onToggleSelect,
  onFocus,
  onContextMenu,
  isPhone,
}: CardRowProps) {
  const flag = flagColour(row.flag);

  if (isPhone) {
    return (
      <PhoneCardRow
        row={row}
        selected={selected}
        focused={focused}
        onToggleSelect={onToggleSelect}
        onFocus={onFocus}
        onContextMenu={onContextMenu}
      />
    );
  }

  return (
    <div
      data-check="cards.row"
      onClick={onFocus}
      onContextMenu={onContextMenu}
      className={[
        "flex cursor-default items-center gap-3 border-b border-line-soft py-[9px] pl-[18px] pr-[18px] text-left",
        focused ? "bg-panel shadow-[inset_3px_0_0_var(--accent)]" : "",
      ].join(" ")}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        onClick={(e) => e.stopPropagation()}
        aria-label={`${row.headword} を選択`}
        className="size-[15px] shrink-0 accent-[var(--accent)]"
      />

      {/* A 3px stripe rather than a dot: it reads down a long list without adding a column. */}
      <span
        aria-label={flag ? (flagName(row.flag) ?? undefined) : undefined}
        className="h-[18px] w-[3px] shrink-0 rounded-[var(--r-2)]"
        style={{ background: flag ?? "transparent" }}
      />

      <span className="w-[112px] shrink-0 truncate font-jp text-[18px]/[1.2] text-ink">
        {row.headword}
      </span>
      <span className="w-[96px] shrink-0 truncate font-jp text-secondary text-ink-3">
        {row.reading}
      </span>
      <span className="min-w-0 flex-1 truncate text-[15px]/[1.4] text-ink-2">{row.gloss}</span>
      <span className="w-[104px] shrink-0 truncate font-mono text-[14px]/[1.4] text-ink-4">
        {row.tags.join(" ")}
      </span>
      {/* The COLUMN is 92px and centred; the pill hugs its own text inside it. Stretched to the
          full column it reads as a button, and nothing in this row is pressable. */}
      <span className="flex w-[92px] shrink-0 justify-center">
        <span
          className={[
            "rounded-[var(--r-2)] border px-[7px] py-[2px] text-[14px]/[1.3] font-semibold",
            STATE_STYLE[row.state],
          ].join(" ")}
        >
          {STATE_LABEL[row.state]}
        </span>
      </span>
      {/* **104px, and it was 74 — too narrow even in English.** Measured in the real font: the
          widest value this cell can hold is `2027年12月31日` at 103px, and the widest it held
          *before* the dates became Japanese was `4 Aug 2027` at 80px, which already overflowed a
          74px box. Nothing reported it because the span has no `truncate` and the row no
          `overflow-hidden`, so the text simply drew over its neighbour and read as a wide column.
          The width comes out of the gloss, which is the row's one `flex-1`. */}
      <span className="w-[104px] shrink-0 text-right font-mono text-[14px]/[1.4] text-ink-2">
        {dueLabel(row)}
      </span>
      <span className="w-[54px] shrink-0 text-right font-mono text-[14px]/[1.4] text-ink-4">
        {row.lapses || ""}
      </span>
    </div>
  );
}
